/**
 * Flush repeating-section draft rows after Create Request save/submit (#996 / #1333).
 * Mirrors the rack draft flush wired in CreateRequest.tsx (#995).
 *
 * Child-table steps (Large UPS, Power Distribution, Small UPS, Cooling, Liquid Cooling)
 * are flushed via dedicated draft services after the parent request is saved.
 */

import { flushDraftCoolingSystemConfigurations } from './coolingSystemConfigurationDraft';
import { toCreateCoolingSystemPayload } from './coolingSystemPayload';
import { flushDraftLargeUpsConfigurations } from './largeUpsConfigurationDraft';
import { toCreateLargeUpsPayload } from './largeUpsPayload';
import { flushDraftLiquidCoolingCduConfigurations } from './liquidCoolingCduConfigurationDraft';
import { toCreateLiquidCoolingCduPayload } from './liquidCoolingCduPayload';
import { remapDraftLocationTagIds } from './locationTagDraftFlush';
import { offlineCache } from './offlineCache';
import { flushDraftPowerDistributionConfigurations } from './powerDistributionConfigurationDraft';
import { toCreatePowerDistributionPayload } from './powerDistributionPayload';
import { toCreateRackPayload } from './rackPayload';
import { flushDraftSmallUpsConfigurations } from './smallUpsConfigurationDraft';
import { toCreateSmallUpsPayload } from './smallUpsPayload';
import { groupRacksByStatus } from '../components/racks/rackListUtils';
import { coolingSystemConfigurationsKeys } from '../hooks/queries/useCoolingSystemConfigurations';
import { largeUpsConfigurationsKeys } from '../hooks/queries/useLargeUpsConfigurations';
import { liquidCoolingCduConfigurationsKeys } from '../hooks/queries/useLiquidCoolingCduConfigurations';
import { offlineRequestsKeys } from '../hooks/queries/useOfflineRequestsQuery';
import { powerDistributionConfigurationsKeys } from '../hooks/queries/usePowerDistributionConfigurations';
import { rackConfigurationsKeys } from '../hooks/queries/useRackConfigurations';
import { smallUpsConfigurationsKeys } from '../hooks/queries/useSmallUpsConfigurations';
import { parseDimensionUnits } from '../request/dimensionUnitChoices';

import type { Request } from '../types';
import type {
  AggregateFamilyKey,
  AggregateFamilyOps,
} from '../types/aggregateSave';
import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { FacilityPowerInstance } from '../types/facilityPowerInstance';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';
import type { QueryClient } from '@tanstack/react-query';

export interface CreateRequestRepeatableDrafts {
  facilityPowerInstances: FacilityPowerInstance[];
  largeUpsConfigurations: LargeUpsConfiguration[];
  powerDistributionConfigurations: PowerDistributionConfiguration[];
  rackConfigurations: RackConfiguration[];
  smallUpsConfigurations: SmallUpsConfiguration[];
  coolingSystemConfigurations: CoolingSystemConfiguration[];
  liquidCoolingCduConfigurations: LiquidCoolingCduConfiguration[];
}

/** Client-only until Facility Power has a server child table (#1154 / #1333). */
export const FACILITY_POWER_INSTANCES_CACHE_KEY =
  'facilityPowerInstances' as const;

const REPEATABLE_CONFIGURATION_DRAFT_KEYS = [
  'rackConfigurations',
  'smallUpsConfigurations',
  'coolingSystemConfigurations',
  'liquidCoolingCduConfigurations',
] as const;

export function extractCreateRequestRepeatableDrafts(
  processedData: Record<string, unknown>,
): {
  requestDataWithoutRepeatableDrafts: Record<string, unknown>;
  drafts: CreateRequestRepeatableDrafts;
} {
  // #1154 legacy *Instances migration removed (P3b Slice 2, pre-#1333 window closed).
  const migrated = processedData;
  const {
    largeUpsConfigurations,
    powerDistributionConfigurations,
    rackConfigurations,
    smallUpsConfigurations,
    coolingSystemConfigurations,
    liquidCoolingCduConfigurations,
    [FACILITY_POWER_INSTANCES_CACHE_KEY]: facilityPowerInstances,
    ...requestDataWithoutRepeatableDrafts
  } = migrated;

  return {
    requestDataWithoutRepeatableDrafts,
    drafts: {
      facilityPowerInstances: Array.isArray(facilityPowerInstances)
        ? (facilityPowerInstances as FacilityPowerInstance[])
        : [],
      largeUpsConfigurations: Array.isArray(largeUpsConfigurations)
        ? (largeUpsConfigurations as LargeUpsConfiguration[])
        : [],
      powerDistributionConfigurations: Array.isArray(
        powerDistributionConfigurations,
      )
        ? (powerDistributionConfigurations as PowerDistributionConfiguration[])
        : [],
      rackConfigurations: Array.isArray(rackConfigurations)
        ? (rackConfigurations as RackConfiguration[])
        : [],
      smallUpsConfigurations: Array.isArray(smallUpsConfigurations)
        ? (smallUpsConfigurations as SmallUpsConfiguration[])
        : [],
      coolingSystemConfigurations: Array.isArray(coolingSystemConfigurations)
        ? (coolingSystemConfigurations as CoolingSystemConfiguration[])
        : [],
      liquidCoolingCduConfigurations: Array.isArray(
        liquidCoolingCduConfigurations,
      )
        ? (liquidCoolingCduConfigurations as LiquidCoolingCduConfiguration[])
        : [],
    },
  };
}

/** Attach draft configuration arrays to a saved request for cache/offline. */
export function mergeRepeatableConfigurationDraftsOntoRequest<
  T extends Record<string, unknown>,
>(request: T, drafts: CreateRequestRepeatableDrafts): T {
  const patch: Record<string, unknown> = {};
  for (const key of REPEATABLE_CONFIGURATION_DRAFT_KEYS) {
    const units = drafts[key as keyof CreateRequestRepeatableDrafts];
    if (!Array.isArray(units)) {
      continue;
    }
    const hadCachedUnits =
      Array.isArray(request[key]) && (request[key] as unknown[]).length > 0;
    if (units.length > 0 || hadCachedUnits) {
      patch[key] = units;
    }
  }
  const facilityPowerInstances = drafts.facilityPowerInstances;
  const hadCachedFacilityPower =
    Array.isArray(request[FACILITY_POWER_INSTANCES_CACHE_KEY]) &&
    (request[FACILITY_POWER_INSTANCES_CACHE_KEY] as unknown[]).length > 0;
  if (
    (Array.isArray(facilityPowerInstances) &&
      facilityPowerInstances.length > 0) ||
    hadCachedFacilityPower
  ) {
    patch[FACILITY_POWER_INSTANCES_CACHE_KEY] = facilityPowerInstances;
  }
  return Object.keys(patch).length > 0 ? { ...request, ...patch } : request;
}

/** After Create Request save/submit — prime React Query with draft configuration rows. */
export function primeCreateRequestRepeatableDraftsInCache(
  queryClient: QueryClient,
  savedRequest: { id: string | number },
  drafts: CreateRequestRepeatableDrafts,
): Record<string, unknown> {
  const primedRequest = mergeRepeatableConfigurationDraftsOntoRequest(
    savedRequest as Record<string, unknown>,
    drafts,
  );
  const idKey = String(savedRequest.id);
  queryClient.setQueryData(offlineRequestsKeys.detail(idKey), primedRequest);
  queryClient.setQueryData(offlineRequestsKeys.lists(), (oldData: unknown) => {
    if (!Array.isArray(oldData)) {
      return [primedRequest];
    }
    return [
      primedRequest,
      ...oldData.filter(
        (item: { id?: string | number }) => String(item.id) !== idKey,
      ),
    ];
  });
  const racks = drafts.rackConfigurations;
  if (Array.isArray(racks) && racks.length > 0) {
    queryClient.setQueryData(rackConfigurationsKeys.list(idKey), racks);
    queryClient.setQueryData(
      rackConfigurationsKeys.grouped(idKey),
      groupRacksByStatus(racks),
    );
  }
  return primedRequest;
}

/** Persist configuration draft arrays after Create Request save/submit. */
export async function persistCreateRequestRepeatableDraftsAfterSave(
  requestId: string | number,
  drafts: CreateRequestRepeatableDrafts,
  isOfflineRequest: boolean,
  savedRequest?: Record<string, unknown>,
): Promise<void> {
  if (isOfflineRequest) {
    await offlineCache.updateCachedRequestRepeatableConfigurations(
      String(requestId),
      {
        facilityPowerInstances: drafts.facilityPowerInstances,
        rackConfigurations: drafts.rackConfigurations,
        smallUpsConfigurations: drafts.smallUpsConfigurations,
        coolingSystemConfigurations: drafts.coolingSystemConfigurations,
        liquidCoolingCduConfigurations: drafts.liquidCoolingCduConfigurations,
      },
    );
    return;
  }

  if (savedRequest) {
    const merged = mergeRepeatableConfigurationDraftsOntoRequest(
      savedRequest,
      drafts,
    );
    await offlineCache.upsertCachedRequest(merged as Request);
    return;
  }
  await offlineCache.updateCachedRequestRepeatableConfigurations(
    String(requestId),
    {
      facilityPowerInstances: drafts.facilityPowerInstances,
      rackConfigurations: drafts.rackConfigurations,
      smallUpsConfigurations: drafts.smallUpsConfigurations,
      coolingSystemConfigurations: drafts.coolingSystemConfigurations,
      liquidCoolingCduConfigurations: drafts.liquidCoolingCduConfigurations,
    },
  );
}

/**
 * Keep client-side configuration rows only when server GET omits the field.
 * An explicit server array is authoritative, including `[]` after a restore.
 */
export function preserveRepeatableConfigurationsFromCachedRequest<
  T extends Record<string, unknown>,
>(serverRequest: T, cachedRequest: T | null | undefined): T {
  if (!cachedRequest) {
    return serverRequest;
  }
  // #1154 legacy *Instances migration removed (P3b Slice 2, pre-#1333 window closed).
  const migratedCache = cachedRequest;
  const patch: Record<string, unknown> = {};
  for (const key of REPEATABLE_CONFIGURATION_DRAFT_KEYS) {
    const serverUnits = serverRequest[key];
    const cachedUnits = migratedCache[key];
    if (
      !Array.isArray(serverUnits) &&
      Array.isArray(cachedUnits) &&
      cachedUnits.length > 0
    ) {
      patch[key] = cachedUnits;
    }
  }
  const serverFacilityPower = serverRequest[FACILITY_POWER_INSTANCES_CACHE_KEY];
  const cachedFacilityPower = migratedCache[FACILITY_POWER_INSTANCES_CACHE_KEY];
  if (
    !Array.isArray(serverFacilityPower) &&
    Array.isArray(cachedFacilityPower) &&
    cachedFacilityPower.length > 0
  ) {
    patch[FACILITY_POWER_INSTANCES_CACHE_KEY] = cachedFacilityPower;
  }
  return Object.keys(patch).length > 0
    ? { ...serverRequest, ...patch }
    : serverRequest;
}

export async function flushCreateRequestRepeatableDrafts(
  requestId: string | number,
  drafts: CreateRequestRepeatableDrafts,
  options: {
    isOfflineRequest?: boolean;
    queryClient?: QueryClient;
    locationTagIdMap?: ReadonlyMap<number, number>;
  } = {},
): Promise<string[]> {
  const warnings: string[] = [];
  const { isOfflineRequest = false, queryClient, locationTagIdMap } = options;
  const idKey = String(requestId);
  const remappedDrafts: CreateRequestRepeatableDrafts = locationTagIdMap
    ? {
        ...drafts,
        smallUpsConfigurations: remapDraftLocationTagIds(
          drafts.smallUpsConfigurations,
          locationTagIdMap,
        ),
        largeUpsConfigurations: remapDraftLocationTagIds(
          drafts.largeUpsConfigurations,
          locationTagIdMap,
        ),
        coolingSystemConfigurations: remapDraftLocationTagIds(
          drafts.coolingSystemConfigurations,
          locationTagIdMap,
        ),
        liquidCoolingCduConfigurations: remapDraftLocationTagIds(
          drafts.liquidCoolingCduConfigurations,
          locationTagIdMap,
        ),
        powerDistributionConfigurations: remapDraftLocationTagIds(
          drafts.powerDistributionConfigurations,
          locationTagIdMap,
        ),
      }
    : drafts;

  if (remappedDrafts.largeUpsConfigurations.length > 0) {
    try {
      await flushDraftLargeUpsConfigurations(
        requestId,
        remappedDrafts.largeUpsConfigurations,
        isOfflineRequest,
      );
      await queryClient?.invalidateQueries({
        queryKey: largeUpsConfigurationsKeys.list(idKey),
      });
      await queryClient?.invalidateQueries({
        queryKey: largeUpsConfigurationsKeys.grouped(idKey),
      });
    } catch (error) {
      console.error('Failed to save draft large UPS configurations:', error);
      warnings.push('large UPS');
    }
  }

  if (remappedDrafts.powerDistributionConfigurations.length > 0) {
    try {
      await flushDraftPowerDistributionConfigurations(
        requestId,
        remappedDrafts.powerDistributionConfigurations,
        isOfflineRequest,
      );
      await queryClient?.invalidateQueries({
        queryKey: powerDistributionConfigurationsKeys.list(idKey),
      });
      await queryClient?.invalidateQueries({
        queryKey: powerDistributionConfigurationsKeys.grouped(idKey),
      });
    } catch (error) {
      console.error(
        'Failed to save draft power distribution configurations:',
        error,
      );
      warnings.push('power distribution');
    }
  }

  if (remappedDrafts.smallUpsConfigurations.length > 0) {
    try {
      await flushDraftSmallUpsConfigurations(
        requestId,
        remappedDrafts.smallUpsConfigurations,
        isOfflineRequest,
      );
      await queryClient?.invalidateQueries({
        queryKey: smallUpsConfigurationsKeys.list(idKey),
      });
      await queryClient?.invalidateQueries({
        queryKey: smallUpsConfigurationsKeys.grouped(idKey),
      });
    } catch (error) {
      console.error('Failed to save draft small UPS configurations:', error);
      warnings.push('small UPS');
    }
  }

  if (remappedDrafts.coolingSystemConfigurations.length > 0) {
    try {
      await flushDraftCoolingSystemConfigurations(
        requestId,
        remappedDrafts.coolingSystemConfigurations,
        isOfflineRequest,
      );
      await queryClient?.invalidateQueries({
        queryKey: coolingSystemConfigurationsKeys.list(idKey),
      });
      await queryClient?.invalidateQueries({
        queryKey: coolingSystemConfigurationsKeys.grouped(idKey),
      });
    } catch (error) {
      console.error('Failed to save draft cooling configurations:', error);
      warnings.push('cooling');
    }
  }

  if (remappedDrafts.liquidCoolingCduConfigurations.length > 0) {
    try {
      await flushDraftLiquidCoolingCduConfigurations(
        requestId,
        remappedDrafts.liquidCoolingCduConfigurations,
        isOfflineRequest,
      );
      await queryClient?.invalidateQueries({
        queryKey: liquidCoolingCduConfigurationsKeys.list(idKey),
      });
      await queryClient?.invalidateQueries({
        queryKey: liquidCoolingCduConfigurationsKeys.grouped(idKey),
      });
    } catch (error) {
      console.error(
        'Failed to save draft liquid cooling CDU configurations:',
        error,
      );
      warnings.push('liquid cooling');
    }
  }

  return warnings;
}

// ─────────────────────────────────────────────────────────────────────────────
// #1501 aggregate create — drafts → one POST /:id/save payload
// ─────────────────────────────────────────────────────────────────────────────

interface DraftFamilyWiring {
  draftKey: keyof CreateRequestRepeatableDrafts;
  familyKey: AggregateFamilyKey;
  toCreatePayload: (row: Record<string, unknown>) => Record<string, unknown>;
}

const DRAFT_FAMILY_WIRINGS: DraftFamilyWiring[] = [
  {
    draftKey: 'rackConfigurations',
    familyKey: 'racks',
    toCreatePayload: toCreateRackPayload as never,
  },
  {
    draftKey: 'smallUpsConfigurations',
    familyKey: 'smallUps',
    toCreatePayload: toCreateSmallUpsPayload as never,
  },
  {
    draftKey: 'largeUpsConfigurations',
    familyKey: 'largeUps',
    toCreatePayload: toCreateLargeUpsPayload as never,
  },
  {
    draftKey: 'coolingSystemConfigurations',
    familyKey: 'coolingSystems',
    toCreatePayload: toCreateCoolingSystemPayload as never,
  },
  {
    draftKey: 'liquidCoolingCduConfigurations',
    familyKey: 'liquidCoolingCdus',
    toCreatePayload: toCreateLiquidCoolingCduPayload as never,
  },
  {
    draftKey: 'powerDistributionConfigurations',
    familyKey: 'powerDistribution',
    toCreatePayload: toCreatePowerDistributionPayload as never,
  },
];

const draftClientKey = (
  familyKey: AggregateFamilyKey,
  row: { id?: number },
  index: number,
): string => `${familyKey}:${row.id != null ? String(row.id) : `i${index}`}`;

/** Parent segment so unit chips ride with the family creates and get remapped. */
export function aggregateCreateDimensionUnitsSegment(dimensionUnits: unknown): {
  request?: { dimensionUnits: Record<string, string> };
} {
  const parsed = parseDimensionUnits(dimensionUnits);
  return Object.keys(parsed).length > 0
    ? { request: { dimensionUnits: parsed } }
    : {};
}

/**
 * Serialize the create-flow draft rows (all six server-backed families;
 * facility power stays client-only) into aggregate-save family segments.
 * clientKeys are family-namespaced — temp ids are only unique per family.
 */
export function buildAggregateCreateFamilySegments(
  drafts: CreateRequestRepeatableDrafts,
  locationTagIdMap: ReadonlyMap<number, number> | undefined,
  requestId: string | number,
): Partial<Record<AggregateFamilyKey, AggregateFamilyOps>> {
  const numericRequestId = parseInt(String(requestId), 10);
  const segments: Partial<Record<AggregateFamilyKey, AggregateFamilyOps>> = {};
  for (const wiring of DRAFT_FAMILY_WIRINGS) {
    const rows = drafts[wiring.draftKey] as unknown as Array<
      { id?: number } & Record<string, unknown>
    >;
    if (!rows || rows.length === 0) {
      continue;
    }
    const remapped = locationTagIdMap
      ? (remapDraftLocationTagIds(
          rows as never,
          locationTagIdMap,
        ) as typeof rows)
      : rows;
    segments[wiring.familyKey] = {
      create: remapped.map((row, index) => ({
        ...wiring.toCreatePayload({ ...row, requestId: numericRequestId }),
        clientKey: draftClientKey(wiring.familyKey, row, index),
      })),
    };
  }
  return segments;
}

/**
 * Swap draft temp ids for the aggregate response's real ids so the post-save
 * cache mirror (persistCreateRequestRepeatableDraftsAfterSave) stores durable
 * rows — the same normalization the per-family draft flushes performed.
 */
export function applyAggregateCreateIdMap(
  drafts: CreateRequestRepeatableDrafts,
  idMap: Record<string, number>,
): CreateRequestRepeatableDrafts {
  const next = { ...drafts };
  for (const wiring of DRAFT_FAMILY_WIRINGS) {
    const rows = drafts[wiring.draftKey] as unknown as Array<
      { id?: number } & Record<string, unknown>
    >;
    if (!rows || rows.length === 0) {
      continue;
    }
    (next[wiring.draftKey] as unknown) = rows.map((row, index) => {
      const realId = idMap[draftClientKey(wiring.familyKey, row, index)];
      return realId != null ? { ...row, id: realId } : row;
    });
  }
  return next;
}

/** Post-aggregate-create row-query invalidation for all six families. */
export async function invalidateRepeatableFamilyQueries(
  queryClient: QueryClient,
  requestId: string | number,
): Promise<void> {
  const idKey = String(requestId);
  const keys = [
    rackConfigurationsKeys.list(idKey),
    rackConfigurationsKeys.grouped?.(idKey),
    smallUpsConfigurationsKeys.list(idKey),
    smallUpsConfigurationsKeys.grouped(idKey),
    largeUpsConfigurationsKeys.list(idKey),
    largeUpsConfigurationsKeys.grouped(idKey),
    coolingSystemConfigurationsKeys.list(idKey),
    coolingSystemConfigurationsKeys.grouped(idKey),
    liquidCoolingCduConfigurationsKeys.list(idKey),
    liquidCoolingCduConfigurationsKeys.grouped(idKey),
    powerDistributionConfigurationsKeys.list(idKey),
    powerDistributionConfigurationsKeys.grouped(idKey),
  ].filter(Boolean) as Array<readonly unknown[]>;
  for (const queryKey of keys) {
    await queryClient.invalidateQueries({ queryKey });
  }
}
